// TransformSelector.js (updated to parse new 'skills' format)
import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const TIER_LABELS = ['static', 'basic', 'advanced', 'expert', 'master', 'grandmaster', 'mythic'];

const TransformSelector = ({ token, transforms, selectedId, setSelectedId, setFormData }) => {
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const selectRef = useRef();

    useEffect(() => {
        selectRef.current?.focus();
    }, []);

    useEffect(() => {
        const baseOptions = transforms.map((t) => ({
            value: t.transformId,
            label: t.name
        }));

        const matching = baseOptions.filter((opt) =>
            opt.label.toLowerCase().includes(query.toLowerCase())
        );

        const showCreate =
            query &&
            !baseOptions.some((opt) => opt.label.toLowerCase() === query.toLowerCase());

        const newOptions = showCreate
            ? [
                {
                    value: '__create__',
                    label: `Create "${query}" Transform`,
                    isNew: true,
                    __custom__: true
                },
                ...matching
            ]
            : matching;

        setOptions(newOptions);
    }, [transforms, query]);

    useEffect(() => {
        if (selectedId) {
            const match = transforms.find((t) => t.transformId === selectedId);
            if (match) setSelectedOption({ value: match.transformId, label: match.name });
        } else if (query) {
            setSelectedOption({ value: '__create__', label: `Create "${query}" Transform` });
        } else {
            setSelectedOption(null);
        }
    }, [selectedId, transforms]);

    const handleChange = async (option, { action }) => {
        if (!option) {
            setSelectedOption(null);
            setSelectedId(null);
            setFormData({ name: query, description: '', skills: {} });
            toast.info('Selection cleared');
            return;
        }

        if (option.isNew || option.value === '__create__') {
            setSelectedOption(option);
            setSelectedId(null);
            setFormData({ name: query, description: '', skills: {} });
            toast.info(`Creating new transform: "${query}"`);
            return;
        }

        setSelectedOption(option);
        setSelectedId(option.value);
        setQuery(option.label);
        toast.info(`Loading transform: ${option.label}...`);

        try {
            const res = await axios.get(`${API_BASE_URL}/transforms/${option.value}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const grouped = {};
            for (const skill of res.data.skills || []) {
                const tierIndex = skill.tier ?? 0;
                const tierLabel = TIER_LABELS[tierIndex] || 'unknown';
                if (!grouped[tierLabel]) grouped[tierLabel] = [];
                grouped[tierLabel].push({
                    skillId: skill.skillId,
                    name: skill.name,
                    notes: skill.notes,
                    isPassive: !!skill.isPassive,
                    isDefaultUnlocked: !!skill.isDefaultUnlocked
                });
            }

            setFormData({
                transformId: res.data.transformId,
                name: res.data.name,
                description: res.data.description,
                skills: grouped
            });

            toast.success(`Loaded transform: ${option.label}`);
        } catch (err) {
            console.error('Failed to fetch transform', err);
            toast.error('Failed to load transform');
        }
    };

    const customStyles = {
        control: (provided) => ({
            ...provided,
            padding: '1.25rem',
            fontSize: '1.5rem',
            borderColor: '#6366f1',
            boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.3)',
            borderRadius: '1rem',
        }),
        placeholder: (provided) => ({
            ...provided,
            fontSize: '1.5rem',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 50,
            borderRadius: '1rem',
            border: '1px solid #6366f1',
            boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15)',
        }),
        option: (provided, state) => ({
            ...provided,
            fontSize: '1.25rem',
            padding: '0.75rem 1.25rem',
            backgroundColor: state.isFocused
                ? '#eef2ff'
                : state.data.__custom__
                    ? '#f0fdfa'
                    : 'white',
            color: '#1e1e1e',
            cursor: 'pointer',
            fontStyle: state.data.__custom__ ? 'italic' : 'normal'
        })
    };

    return (
        <div className="min-h-screen flex flex-col justify-center items-center">
            <h1 className="text-4xl font-bold mb-6 text-center">Transform Manager</h1>
            <div className="w-full max-w-2xl px-4">
                <Select
                    id="transform-select"
                    ref={selectRef}
                    options={options}
                    value={selectedOption}
                    inputValue={query}
                    onInputChange={(value, { action }) => {
                        if (action !== 'input-blur' && action !== 'menu-close') {
                            setQuery(value);
                        }
                    }}
                    onChange={handleChange}
                    isClearable
                    placeholder="Search or create transform"
                    onMenuOpen={() => toast.info('Browsing transforms...')}
                    styles={customStyles}
                />
            </div>
        </div>
    );
};

export default TransformSelector;
